import React, { useState, useEffect, useRef } from 'react';
import {
  X, User, Mail, Phone, CalendarDays, Clock, MapPin, Star, Users, Camera
} from 'lucide-react';

const HealthSection = () => {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  const features = [
    { icon: <MapPin className="w-5 h-5" />, text: "3 hours from Delhi NCR" },
    { icon: <Star className="w-5 h-5" />, text: "Expert guides included" },
    { icon: <Users className="w-5 h-5" />, text: "All skill levels welcome" },
    { icon: <Camera className="w-5 h-5" />, text: "Photo opportunities" }
  ];

  return (
    <section
      id='book-now'
      ref={sectionRef}
      style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}
      className={`relative overflow-hidden pt-28 md:pt-38 bg-white py-24 px-6 transition-opacity transform duration-1000 ease-out ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-black opacity-5"></div>
      </div>

      <div className="relative max-w-6xl mx-auto text-center">
        <div className="inline-flex items-center bg-black text-white rounded-full px-4 py-2 mb-8 shadow-lg border border-gray-300">
          <span className="relative w-3 h-3 mr-2">
            <span className="absolute inset-0 bg-green-500 rounded-full animate-ping"></span>
            <span className="absolute top-1/2 left-1/2 w-2 h-2 bg-green-500 rounded-full transform -translate-x-1/2 -translate-y-1/2"></span>
          </span>
          <span className="text-sm font-medium">Adventure Awaits</span>
        </div>

        <h2 className="text-[32px] font-bold text-black leading-tight mb-8">
          Mountain & Forest<br />
          <span className="text-[32px] font-light">Horse Safari</span>
        </h2>

        <p className="max-w-2xl mx-auto text-[16px] text-gray-600 leading-relaxed mb-12 font-light">
          Discover the thrill of horseback riding through untouched mountain passes and deep forest
          trails – an unforgettable adventure just a short drive away from Delhi NCR.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 max-w-4xl mx-auto">
          {features.map((feature, index) => (
            <div key={index} className="bg-gray-50 rounded-2xl p-4 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 hover:transform hover:-translate-y-1">
              <div className="text-black mb-2 flex justify-center">{feature.icon}</div>
              <p className="text-sm font-medium text-gray-700">{feature.text}</p>
            </div>
          ))}
        </div>

        <button
          onClick={() => setShowModal(true)}
          className="bg-black text-white px-8 py-4 rounded-full font-semibold text-lg shadow-2xl hover:bg-gray-800 transition-all duration-300 hover:scale-105"
        >
          <span className="relative z-10">Book Your Safari Now</span>
        </button>

        <div className="flex items-center justify-center mt-8 space-x-6 text-sm text-gray-600">
          <div className="flex items-center">
            <Star className="w-4 h-4 text-black mr-1" />
            <span>4.8/5 rating</span>
          </div>
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
          <div>1000+ happy riders</div>
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
          <div>100% safe</div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-screen overflow-y-auto relative">
            <button
              onClick={() => {
                setShowModal(false);
                setFormSubmitted(false);
              }}
              className="absolute top-6 right-6 text-gray-400 hover:text-black z-10 bg-gray-100 rounded-full p-2 hover:bg-gray-200 transition-colors duration-200"
            >
              <X size={20} />
            </button>

            <div className="bg-black text-white p-8 rounded-t-3xl">
              <div className="text-center">
                <h3 className="text-[32px] font-bold mb-2">Reserve Your Adventure</h3>
                <p className="text-gray-300 text-[16px]">Book your unforgettable horse safari experience</p>
              </div>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.target;
                const formData = new FormData(form);

                fetch(
                  'https://docs.google.com/forms/u/0/d/1q-_T0LsgetjHtMKkqMyWGLUsfAd4nnWn9rlMnGynpJ0/formResponse',
                  {
                    method: 'POST',
                    mode: 'no-cors',
                    body: formData,
                  }
                )
                  .then(() => {
                    form.reset();
                    setFormSubmitted(true);
                    setTimeout(() => {
                      setFormSubmitted(false);
                      setShowModal(false);
                    }, 1500);
                  })

                  .catch(() => {
                    alert("Something went wrong. Please try again.");
                  });
              }}
              className="p-8"
            >
              <div className="space-y-6">
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2 text-black" />
                    Personal Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="relative">
                      <input
                        type="text"
                        name="entry.867900108"
                        placeholder="Full Name"
                        className="w-full border-2 border-gray-200 rounded-xl px-12 py-4 bg-gray-50 focus:bg-white focus:border-black focus:outline-none transition-colors duration-200"
                        required
                      />
                      <User className="absolute left-4 top-4 text-gray-400" size={20} />
                    </div>
                    <div className="relative">
                      <input
                        type="email"
                        name="entry.1040194355"
                        placeholder="Email Address"
                        className="w-full border-2 border-gray-200 rounded-xl px-12 py-4 bg-gray-50 focus:bg-white focus:border-black focus:outline-none transition-colors duration-200"
                        required
                      />
                      <Mail className="absolute left-4 top-4 text-gray-400" size={20} />
                    </div>
                    <div className="relative">
                      <input
                        type="tel"
                        name="entry.258978532"
                        placeholder="Mobile Number"
                        className="w-full border-2 border-gray-200 rounded-xl px-12 py-4 bg-gray-50 focus:bg-white focus:border-black focus:outline-none transition-colors duration-200"
                        required
                      />
                      <Phone className="absolute left-4 top-4 text-gray-400" size={20} />
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <CalendarDays className="w-5 h-5 mr-2 text-black" />
                    Booking Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="relative">
                      <select
                        name="entry.813393252"
                        className="w-full border-2 border-gray-200 rounded-xl px-6 py-4 bg-gray-50 focus:bg-white focus:border-black focus:outline-none transition-colors duration-200"
                        required
                      >
                        <option>Preferred Experience</option>
                        <option value="Desert Ride">Desert Ride</option>
                        <option value="Farm Stay">Farm Stay</option>
                        <option value="Festival Safari">Festival Safari</option>
                      </select>
                      <Users className="absolute left-2 top-4 text-gray-400 pointer-events-none" size={20} />
                    </div>
                    <div className="relative">
                      <input
                        type="date"
                        name="entry.63492292"
                        className="w-full border-2 border-gray-200 rounded-xl px-12 py-4 bg-gray-50 focus:bg-white focus:border-black focus:outline-none transition-colors duration-200"
                        required
                      />
                      <CalendarDays className="absolute left-4 top-4 text-gray-400" size={20} />
                    </div>
                    <div className="relative">
                      <input
                        type="time"
                        name="entry.1828718306"
                        className="w-full border-2 border-gray-200 rounded-xl px-12 py-4 bg-gray-50 focus:bg-white focus:border-black focus:outline-none transition-colors duration-200"
                        required
                      />
                      <Clock className="absolute left-4 top-4 text-gray-400" size={20} />
                    </div>
                  </div>
                </div>

                <div className="flex justify-center pt-6">
                  {formSubmitted ? (
                    <div className="text-green-600 text-xl font-semibold bg-green-50 p-4 rounded-xl shadow-md">
                      🎉 Your booking was successfully submitted!
                    </div>
                  ) : (
                    <button
                      type="submit"
                      className="bg-black text-white px-12 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 hover:bg-gray-800"
                    >
                      Confirm Booking
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default HealthSection;
